import type { ReactElement } from 'react'
import { useMemo } from 'react'
import {
  SEVEN_DAYS_AFTER_PURCHASE,
  CANCELLATION_POLICIES,
  CANCELLATION_POLICY_DAYS,
  PRIOR_TO_CHECK_IN_CANCELLATION_POLICIES,
} from 'constants/cancellationPolicies'
import type { Moment } from 'moment'
import moment from 'moment'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Colour from 'components/utils/Colour'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'

export function getCancellationPolicyText(
  intl: IntlShape,
  cancellationPolicyType: string,
  offerType: App.OfferType,
  refundableUntil?: Moment,
  showHotelOnly = false,
): { highlightText: string | undefined, secondaryText: string | undefined } {
  const freeCancellationText = intl.formatMessage({
    id: 'Q-Vcg0q9UWQuGYlad6xb',
    defaultMessage: 'Free cancellation',
    description: 'Highlight text for free cancellation policy on search tiles',
  })
  const flexibleCancellationText = intl.formatMessage({
    id: 'hCwWYZZ-Qge1iETJVsZy',
    defaultMessage: 'Flexible cancellation',
    description: 'Highlight text for flexible cancellation policy on search tiles',
  })
  const hotelOnlyText = intl.formatMessage({
    id: 'faGagZXhftXwhuqogaUP',
    defaultMessage: '(hotel only)',
    description: 'Cancellation policy qualifier indicating that only the hotel is covered within a bundled search result',
  })
  const refundableUntilLabel = refundableUntil ?
      intlDateFormatter(refundableUntil, INTL_DAY_MONTH_NAME_SHORT, intl.locale) :
    undefined

  if (offerType == OFFER_TYPE_ALWAYS_ON) {
    if (!refundableUntil) {
      return {
        highlightText: freeCancellationText,
        secondaryText: showHotelOnly ? hotelOnlyText : '',
      }
    }
    return {
      highlightText: freeCancellationText,
      secondaryText: showHotelOnly ? intl.formatMessage(
        {
          id: '7N-5ccqMoBIsCFxjYvBv',
          defaultMessage: 'until {date} (hotel only)',
          description: 'Secondary text for free cancellation showing its deadline and the hotel-only qualifier on bundled search tiles',
        },
        { date: refundableUntilLabel },
      ) : intl.formatMessage(
        {
          id: '_D8fTgUYkBCRG0egD3S8',
          defaultMessage: 'until {date}',
          description: 'Secondary text for free cancellation policy showing refundable-until date on search tiles',
        },
        { date: refundableUntilLabel },
      ),
    }
  }
  if (
    cancellationPolicyType === CANCELLATION_POLICIES.POST_PURCHASE_SEVEN_DAYS
  ) {
    return {
      highlightText: freeCancellationText,
      secondaryText: showHotelOnly ? intl.formatMessage(
        {
          id: 'C1auMrg6p2UZAHnEZzjC',
          defaultMessage: 'within 7 days of purchase (hotel only)',
          description: 'Secondary text for free cancellation after purchase with a hotel-only qualifier on bundled search tiles',
        },
      ) : intl.formatMessage(
        {
          id: 'b0HS_uIKDXd-RNoJwDHe',
          defaultMessage: 'within 7 days of purchase',
          description: 'Secondary text for free cancellation within 7 days of purchase on search tiles',
        },
      ),
    }
  }
  if (PRIOR_TO_CHECK_IN_CANCELLATION_POLICIES.includes(cancellationPolicyType)) {
    if (!refundableUntil) {
      return {
        highlightText: flexibleCancellationText,
        secondaryText: showHotelOnly ? intl.formatMessage({
          id: 'sA-8eF460VpwSGVBy5kf',
          defaultMessage: 'available (hotel only)',
          description: 'Secondary text for flexible cancellation available for hotel only on search tiles',
        }) : intl.formatMessage({
          id: 'yxcSwRy-UcIgLWRi-aJc',
          defaultMessage: 'available',
          description: 'Secondary text indicating flexible cancellation is available on standalone accommodation search tiles',
        }),
      }
    }
    return {
      highlightText: flexibleCancellationText,
      secondaryText: showHotelOnly ? intl.formatMessage(
        {
          id: 'RFzfNQfVSwwXTZV06kTP',
          defaultMessage: 'until {date} (hotel only)',
          description: 'Secondary text for flexible cancellation showing refundable-until date for hotel only on search tiles',
        },
        { date: refundableUntilLabel },
      ) : intl.formatMessage(
        {
          id: 'baTAZufzzwbKIUtm3xcc',
          defaultMessage: 'until {date}',
          description: 'Secondary text for flexible cancellation showing the refundable-until date on standalone accommodation search tiles',
        },
        { date: refundableUntilLabel },
      ),
    }
  }
  return {
    highlightText: undefined,
    secondaryText: undefined,
  }
}

export function getRefundableUntilAndIsRefundableFromCancellationPolicy(
  cancellationPolicyType: App.CancellationPolicyType,
  checkInDate: string | Moment,
  propertyTimezoneOffset: number,
): {
  refundableUntil: Moment | undefined
  isRefundable: boolean
} {
  const days = CANCELLATION_POLICY_DAYS[cancellationPolicyType] || 0

  if (days === 0 || cancellationPolicyType === CANCELLATION_POLICIES.NON_REFUNDABLE) {
    return { refundableUntil: undefined, isRefundable: false }
  }

  let refundableUntil: Moment | undefined

  const checkInMoment = moment(checkInDate)
  if (propertyTimezoneOffset) {
    checkInMoment.utcOffset(propertyTimezoneOffset, true)
  }

  const startBasedOnCheckIn = checkInDate ? checkInMoment.subtract(days, 'days') : undefined

  const today = moment()
  if (propertyTimezoneOffset) {
    today.utcOffset(propertyTimezoneOffset)
  }

  const startBasedOnToday = today.add(SEVEN_DAYS_AFTER_PURCHASE, 'days')

  if (cancellationPolicyType === CANCELLATION_POLICIES.POST_PURCHASE_SEVEN_DAYS) {
    refundableUntil = !startBasedOnCheckIn || startBasedOnToday.isBefore(checkInDate) ?
      startBasedOnToday :
      startBasedOnCheckIn
  } else {
    refundableUntil = startBasedOnCheckIn
  }

  const isRefundable = !refundableUntil || refundableUntil.isAfter(moment())

  return { refundableUntil, isRefundable }
}

interface Props {
  cancellationPolicyType: App.CancellationPolicyType
  checkInDate?: string | Moment
  timezoneOffset?: number
  offerType: App.OfferType
  textSize?: 'small' | 'medium'
  icon?: ReactElement | false
  showHotelOnly?: boolean
}

function TileCancellationPolicy(props: Props) {
  const {
    cancellationPolicyType,
    checkInDate,
    timezoneOffset,
    offerType,
    textSize = 'medium',
    icon,
    showHotelOnly = false,
  } = props
  const intl = useIntl()

  const { highlightText, secondaryText, isRefundable } = useMemo(() => {
    const { refundableUntil, isRefundable } = getRefundableUntilAndIsRefundableFromCancellationPolicy(
      cancellationPolicyType,
      checkInDate || '',
      timezoneOffset || 0,
    )
    return {
      ...getCancellationPolicyText(intl, cancellationPolicyType, offerType, refundableUntil, showHotelOnly),
      isRefundable,
    }
  }, [intl, cancellationPolicyType, checkInDate, timezoneOffset, offerType, showHotelOnly])

  if (!isRefundable) {
    return null
  }

  return <BodyTextBlock startIcon={icon} variant={textSize}>
    <Colour value="highlight-primary-dark" as="b">
      {highlightText}&nbsp;
    </Colour>
    {secondaryText}
  </BodyTextBlock>
}

export default TileCancellationPolicy
